import React, { useEffect, useState, useRef } from 'react';
import {
  getVideos,
  uploadVideo,
  updateVideo,
  deleteVideo,
  reordenarVideos,
  getVideoEspecifico,
} from '../../Api/VideoApi';
import {
  Card,
  Button,
  Badge,
  Spinner,
  Alert,
  Form,
  Modal,
  ProgressBar,
  Row,
  Col,
} from 'react-bootstrap';
import {
  ArrowUp,
  ArrowDown,
  Trash,
  PencilSquare,
  PlayCircle,
  Upload,
  CheckCircle,
} from 'react-bootstrap-icons';

// ─── helpers ──────────────────────────────────────────────────────────────────
const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString('es-DO', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

// ─── Modal: Reproducir video específico ───────────────────────────────────────
function ModalReproducir({ show, onHide, videos }) {
  const [idSeleccionado, setIdSeleccionado] = useState('');
  const [videoData, setVideoData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');

  const handleReproducir = async () => {
    if (!idSeleccionado) return;
    try {
      setCargando(true);
      setError('');
      const data = await getVideoEspecifico(Number(idSeleccionado));
      setVideoData(data);
    } catch {
      setError('No se pudo cargar el video. Verifica que esté activo.');
      setVideoData(null);
    } finally {
      setCargando(false);
    }
  };

  const handleClose = () => {
    setIdSeleccionado('');
    setVideoData(null);
    setError('');
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>▶ Reproducir video específico</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Selecciona un video</Form.Label>
          <Form.Select
            value={idSeleccionado}
            onChange={(e) => { setIdSeleccionado(e.target.value); setVideoData(null); }}
          >
            <option value="">— Elige un video —</option>
            {videos.filter(v => v.activo).map((v) => (
              <option key={v.id} value={v.id}>
                [{v.orden}] {v.titulo}
              </option>
            ))}
          </Form.Select>
        </Form.Group>

        {error && <Alert variant="danger">{error}</Alert>}

        {videoData && (
          <div className="mt-3">
            <p className="text-muted mb-2">
              <strong>{videoData.titulo}</strong>
            </p>
            <video
              src={videoData.video_url}
              controls
              autoPlay
              style={{ width: '100%', borderRadius: '12px', maxHeight: '60vh' }}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Cerrar</Button>
        <Button
          variant="primary"
          onClick={handleReproducir}
          disabled={!idSeleccionado || cargando}
        >
          {cargando ? <Spinner size="sm" animation="border" /> : <><PlayCircle className="me-1" />Reproducir</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Modal: Editar video ───────────────────────────────────────────────────────
function ModalEditar({ show, video, onHide, onSaved }) {
  const [titulo, setTitulo] = useState('');
  const [orden, setOrden] = useState(0);
  const [activo, setActivo] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (video) {
      setTitulo(video.titulo);
      setOrden(video.orden);
      setActivo(video.activo);
      setError('');
    }
  }, [video]);

  const handleGuardar = async () => {
    if (!titulo.trim()) { setError('El título no puede estar vacío.'); return; }
    try {
      setGuardando(true);
      setError('');
      const updated = await updateVideo(video.id, { titulo: titulo.trim(), orden, activo });
      onSaved(updated);
      onHide();
    } catch {
      setError('Error al guardar los cambios. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>Editar video</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && <Alert variant="danger">{error}</Alert>}
        <Form.Group className="mb-3">
          <Form.Label>Título</Form.Label>
          <Form.Control
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Ej: Promo verano 2025"
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Orden de reproducción</Form.Label>
          <Form.Control
            type="number"
            min={0}
            value={orden}
            onChange={(e) => setOrden(Number(e.target.value))}
          />
        </Form.Group>
        <Form.Check
          type="switch"
          label={activo ? 'Activo (se reproduce en sala)' : 'Inactivo (no se reproduce)'}
          checked={activo}
          onChange={(e) => setActivo(e.target.checked)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        <Button variant="primary" onClick={handleGuardar} disabled={guardando}>
          {guardando ? <Spinner size="sm" animation="border" /> : <><CheckCircle className="me-1" />Guardar</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Componente principal ──────────────────────────────────────────────────────
function VideoAdminView() {
  const [videos, setVideos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [exito, setExito] = useState('');

  // Upload
  const [archivo, setArchivo] = useState(null);
  const [titulo, setTitulo] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [progreso, setProgreso] = useState(0);
  const fileInputRef = useRef(null);

  // Modales
  const [videoEditar, setVideoEditar] = useState(null);
  const [showModalReproducir, setShowModalReproducir] = useState(false);

  // Reordenar
  const [reordenando, setReordenando] = useState(false);

  const mostrarExito = (msg) => {
    setExito(msg);
    setTimeout(() => setExito(''), 3500);
  };

  const cargarVideos = async () => {
    try {
      setCargando(true);
      setError('');
      const data = await getVideos();
      setVideos(data);
    } catch {
      setError('No se pudieron cargar los videos. Verifica tu sesión.');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarVideos(); }, []);

  // ── Subir ──────────────────────────────────────────────────────────────────
  const handleSubir = async () => {
    if (!archivo) { setError('Selecciona un archivo de video.'); return; }
    if (!titulo.trim()) { setError('El título es obligatorio.'); return; }

    const formData = new FormData();
    formData.append('video', archivo);
    formData.append('titulo', titulo.trim());

    try {
      setSubiendo(true);
      setError('');
      setProgreso(0);
      const nuevo = await uploadVideo(formData, setProgreso);
      setVideos((prev) => [...prev, nuevo].sort((a, b) => a.orden - b.orden));
      setArchivo(null);
      setTitulo('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      mostrarExito('Video subido correctamente.');
    } catch (e) {
      const msg = e.response?.data?.video?.[0] || e.response?.data?.detail || 'Error al subir el video.';
      setError(msg);
    } finally {
      setSubiendo(false);
      setProgreso(0);
    }
  };

  // ── Eliminar ───────────────────────────────────────────────────────────────
  const handleEliminar = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este video? Esta acción no se puede deshacer.')) return;
    try {
      setError('');
      await deleteVideo(id);
      setVideos((prev) => prev.filter((v) => v.id !== id));
      mostrarExito('Video eliminado.');
    } catch {
      setError('Error al eliminar el video.');
    }
  };

  // ── Edición guardada ───────────────────────────────────────────────────────
  const handleEditSaved = (updated) => {
    setVideos((prev) =>
      prev
        .map((v) => (v.id === updated.id ? updated : v))
        .sort((a, b) => a.orden - b.orden)
    );
    mostrarExito('Video actualizado.');
  };

  // ── Mover en lista (reordenar localmente + guardar) ────────────────────────
  const moverVideo = async (idx, direccion) => {
    const lista = [...videos];
    const swapIdx = idx + direccion;
    if (swapIdx < 0 || swapIdx >= lista.length) return;

    // Intercambiar órdenes
    const ordenA = lista[idx].orden;
    const ordenB = lista[swapIdx].orden;
    lista[idx] = { ...lista[idx], orden: ordenB };
    lista[swapIdx] = { ...lista[swapIdx], orden: ordenA };

    // Si órdenes son iguales, reasignar secuencialmente
    const ordenesNuevos = lista.map((v, i) => ({ id: v.id, orden: i }));
    const videosActualizados = lista
      .map((v, i) => ({ ...v, orden: i }))
      .sort((a, b) => a.orden - b.orden);

    setVideos(videosActualizados);

    try {
      setReordenando(true);
      await reordenarVideos(ordenesNuevos);
      mostrarExito('Orden guardado.');
    } catch {
      setError('Error al guardar el nuevo orden. Recargando lista…');
      cargarVideos();
    } finally {
      setReordenando(false);
    }
  };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="fw-bold mb-0">📺 Videos Publicitarios</h2>
        <Button variant="outline-primary" onClick={() => setShowModalReproducir(true)}>
          <PlayCircle className="me-1" /> Reproducir específico
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {exito && <Alert variant="success">{exito}</Alert>}

      {/* ── Formulario de subida ──────────────────────────────────────────────── */}
      <Card className="mb-4 shadow-sm">
        <Card.Header className="fw-semibold">
          <Upload className="me-2" />Subir nuevo video
        </Card.Header>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Título</Form.Label>
                <Form.Control
                  placeholder="Ej: Promo verano 2025"
                  value={titulo}
                  onChange={(e) => setTitulo(e.target.value)}
                  disabled={subiendo}
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Archivo de video (mp4, webm, mov…)</Form.Label>
                <Form.Control
                  type="file"
                  accept="video/*"
                  ref={fileInputRef}
                  onChange={(e) => setArchivo(e.target.files[0] || null)}
                  disabled={subiendo}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-grid">
              <Button
                variant="primary"
                onClick={handleSubir}
                disabled={subiendo || !archivo || !titulo.trim()}
              >
                {subiendo
                  ? <Spinner size="sm" animation="border" />
                  : <><Upload className="me-1" />Subir</>
                }
              </Button>
            </Col>
          </Row>

          {subiendo && progreso > 0 && (
            <ProgressBar
              animated
              now={progreso}
              label={`${progreso}%`}
              className="mt-3"
            />
          )}
        </Card.Body>
      </Card>

      {/* ── Lista de videos ───────────────────────────────────────────────────── */}
      {cargando ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Cargando videos…</p>
        </div>
      ) : videos.length === 0 ? (
        <Alert variant="info">No hay videos cargados todavía. Sube el primero arriba.</Alert>
      ) : (
        <Card className="shadow-sm">
          <Card.Header className="fw-semibold d-flex justify-content-between align-items-center">
            <span>Lista de videos ({videos.length})</span>
            {reordenando && <Spinner size="sm" animation="border" variant="secondary" />}
          </Card.Header>
          <div className="list-group list-group-flush">
            {videos.map((video, idx) => (
              <div
                key={video.id}
                className="list-group-item d-flex align-items-center gap-3 py-3"
                style={{ opacity: video.activo ? 1 : 0.55 }}
              >
                {/* Controles de orden */}
                <div className="d-flex flex-column gap-1">
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => moverVideo(idx, -1)}
                    disabled={idx === 0 || reordenando}
                    style={{ padding: '2px 6px' }}
                    title="Subir"
                  >
                    <ArrowUp size={14} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-secondary"
                    onClick={() => moverVideo(idx, 1)}
                    disabled={idx === videos.length - 1 || reordenando}
                    style={{ padding: '2px 6px' }}
                    title="Bajar"
                  >
                    <ArrowDown size={14} />
                  </Button>
                </div>

                {/* Badge orden */}
                <Badge bg="secondary" style={{ minWidth: '2rem', textAlign: 'center' }}>
                  {video.orden}
                </Badge>

                {/* Info */}
                <div className="flex-grow-1">
                  <div className="fw-semibold">{video.titulo}</div>
                  <small className="text-muted">
                    Subido: {fmt(video.subido_en)} &nbsp;·&nbsp; ID: {video.id}
                  </small>
                </div>

                {/* Estado */}
                <Badge bg={video.activo ? 'success' : 'secondary'}>
                  {video.activo ? 'Activo' : 'Inactivo'}
                </Badge>

                {/* Acciones */}
                <div className="d-flex gap-2">
                  <Button
                    size="sm"
                    variant="outline-primary"
                    title="Editar"
                    onClick={() => setVideoEditar(video)}
                  >
                    <PencilSquare size={15} />
                  </Button>
                  <Button
                    size="sm"
                    variant="outline-danger"
                    title="Eliminar"
                    onClick={() => handleEliminar(video.id)}
                  >
                    <Trash size={15} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* ── Modales ───────────────────────────────────────────────────────────── */}
      <ModalEditar
        show={!!videoEditar}
        video={videoEditar}
        onHide={() => setVideoEditar(null)}
        onSaved={handleEditSaved}
      />

      <ModalReproducir
        show={showModalReproducir}
        onHide={() => setShowModalReproducir(false)}
        videos={videos}
      />
    </div>
  );
}

export default VideoAdminView;
